<i18n>
{
  "ja": {
    "scroll": "詳細を見る",
    "recommend": {
      "hd": "こんな方におすすめの{newline-sp}キャンペーンです"
    },
    "campaign": {
      "hd": "🎉 キャンペーン内容 🎉"
    },
    "howTo": {
      "hd": "キャンペーン参加方法",
      "btn": "無料登録してキャンペーンに参加"
    },
    "form": {
      "hd": "申し込みフォーム"
    },
    "howToUse": {
      "hd": "無料登録後の利用イメージ"
    },
    "step1": {
      "hd": "全国から倉庫を検索",
      "body": "保管単価と作業単価を見ながら、{newline-sp}倉庫を比較検討できます。"
    },
    "step2": {
      "hd": "倉庫の空きを一括確認",
      "body": "倉庫に空きスペースがあるかどうか、{newline-sp}まとめて確認できます。"
    },
    "step3": {
      "hd": "確定したらすぐに利用",
      "body": "契約を終えたら、あとは商品を入荷するだけで{newline-sp}すぐに利用できます。"
    },
    "btn": {
      "more": "WareXをもっと知る",
      "register": "無料登録して倉庫を探す"
    },
    "contact": {
      "head": "倉庫の手配でお探しの方は、{newline}いつでもお気軽にご相談ください。",
      "hours": "受付時間　平日9:00〜17:00"
    }
  }
}
</i18n>

<template>
  <div>
    <main class="kv overflow-hidden relative flex items-center justify-center">
      <div class="relative flex w-full">
        <div class="relative md:min-h-0 pb-24 lg:pb-6 lg:min-h-0 flex flex-col lg:flex-row justify-center lg:justify-start lg:items-center max-w-6xl w-7/8 mx-auto">
          <div v-if="article.kvImage" class="lg:absolute md:right-0 xl:right-auto xl:left-1/2 lg:top-1/2 lg:transform lg:-translate-y-1/2 lg:flex lg:min-h-full md:w-5/12 justify-center items-center mx-auto">
            <img :src="$options.filters.cmsURL(article.kvImage.url)" alt="" class="mx-auto my-6 lg:m-0" width="500">
          </div>
          <div class="relative lg:ml-0 fade mx-auto lg:w-7/12 xl:w-6/12">
            <p v-if="article.sub" class="mb-4 text-center md:text-left">
              <span class="bg-white text-primary-dark font-bold rounded-full py-3 px-6 inline-block leading-none">{{ article.sub }}</span>
            </p>
            <h1 class="font-semibold text-black text-center md:text-left leading-loose">
              <span class="text-2xl md:text-4xl whitespace-pre-wrap border-b-4 border-primary-dark">{{ article.title }}</span>
            </h1>
            <div class="md max-w-lg text-black leading-relaxed mt-5" v-html="$md.render(article.lead)" />
          </div>
        </div>
      </div>
      <div class="absolute inset-x-0 bottom-0 mb-6 text-center text-black">
        <span class="inline-block relative pt-2 text-xs md:text-sm">{{ $t("scroll") }}</span>
        <img class="mt-2 mx-auto" src="~assets/img/index/scrollIcon.svg" width="20" />
      </div>
    </main>
    <div v-if="article.recommendList.length > 0" class="overflow-hidden bg-gray-100 py-12 text-black">
      <div class="flex flex-wrap items-center container w-7/8 lg:w-auto mx-auto">
        <div class="max-w-xl mx-auto my-8">
          <div class="head text-center">
            <i18n path="recommend.hd" tag="h2" class="text-xl md:text-2xl text-black font-bold">
              <template #newline-sp><br class="md:hidden"></template>
            </i18n>
          </div>
          <ul class="mt-5 mb-3 md:my-8 issues">
            <li
              v-for="(item, index) in article.recommendList"
              :key="index"
              v-intersect="{
                callback: onIntersect,
                threshold: [0.5]
              }"
              class="my-4 bg-white rounded-lg py-4 px-2 md:px-6 flex items-center tracking-tight md:text-base fade"
            >
              <i class="material-icons mr-3 text-primary-dark">check_box</i>
              <p class="flex-grow text-base md:text-lg tracking-wide"><strong>{{ item.title }}</strong></p>
            </li>
          </ul>
        </div>
        <div class="pr-4 pl-10 lg:px-12 w-1/2 lg:w-3/12 lg:order-first">
          <img class="mx-auto" src="~assets/img/index/solutions1.png" srcset="~assets/img/index/solutions1@2x.png 2x" alt="">
        </div>
        <div class="pr-10 pl-4 lg:px-12 w-1/2 lg:w-3/12">
          <img class="mx-auto" src="~assets/img/index/solutions2.png" srcset="~assets/img/index/solutions2@2x.png 2x" alt="">
        </div>
      </div>
    </div>
    <section v-if="article.contentsDetail || article.contentsDetails.length > 0" class="my-24 text-black">
      <h2 class="mb-12 text-center text-black">
        <span class="border-b-4 border-primary-dark text-2xl font-bold">{{ $t("campaign.hd") }}</span>
      </h2>
      <div class="max-w-xl w-7/8 mx-auto">
        <div v-if="article.contentsDetail" class="md mb-6 md:mb-3" v-html="$md.render(article.contentsDetail)" />
        <div v-if="article.contentsDetails" class="rounded bg-gray-100 pt-12 pb-1 px-6 md:px-12 text-center">
          <div
            v-for="(item, index) in article.contentsDetails"
            :key="index"
            class="mb-12"
          >
            <h3 class="mb-6 text-black">
              <span class="font-bold border-b-3 border-primary-dark">{{ item.headline }}</span>
            </h3>
            <div class="md inline-block text-sm text-left" v-html="$md.render(item.body)" />
          </div>
        </div>
      </div>
    </section>
    <section v-if="article.howto.length > 0" class="my-24 text-black">
      <h2 class="mb-12 text-center text-black">
        <span class="border-b-4 border-primary-dark text-2xl font-bold">{{ $t("howTo.hd") }}</span>
      </h2>
      <ol class="steps md:flex justify-center items-start max-w-5xl mx-auto w-7/8 text-black">
        <li
          v-for="(item, index) in article.howto"
          :key="index"
          class="relative md:px-5 text-center md:w-1/4"
        >
          <span class="mb-3 relative block text-secondary leading-none text-5xl font-semibold">
            {{ zp(index + 1, 2) }}
          </span>
          <div class="img-box block mb-3">
            <img :src="$options.filters.cmsURL(item.image.url)" alt="" class="w-full">
          </div>
          <div class="md text-sm leading-relaxed text-left" v-html="$md.render(item.detail)" />
        </li>
      </ol>
      <div class="mx-auto text-center mt-12">
        <button class="inline-flex items-center justify-center max-w-xs bg-orange text-white py-4 px-4 rounded-full text-center" @click="openModalDialog">
          {{ $t("howTo.btn") }}
          <span class="material-icons align-middle ml-4">arrow_forward</span>
        </button>
      </div>
    </section>
    <section v-if="article.formId" class="form w-7/8 max-w-xl mx-auto">
      <h2 class="mb-12 text-center text-black">
        <span class="border-b-4 border-primary-dark text-2xl font-bold">{{ $t("form.hd") }}</span>
      </h2>
      <client-only>
        <div v-if="isLoading" class="text-center">
          <p>Loading...</p>
        </div>
        <hs-form v-else-if="article.formId && portalId" @submit="onSubmit" :form-id="article.formId" :portal-id="portalId">
          <template v-slot:success>
            <contact-success />
          </template>
        </hs-form>
        <div v-else>
          <div v-if="error">
            {{ error }}
          </div>
          <div v-else>
            Server Error
          </div>
        </div>
      </client-only>
    </section>
    <hr class="w-10/12 max-w-lg mx-auto my-24 border-gray-400">
    <section class="howto my-24">
      <div class="text-center w-7/8 mx-auto">
        <h2 class="block-header text-secondary">{{ $t("howToUse.hd") }}</h2>
        <h2 class="text-4xl text-secondary font-bold mb-10">How to Use</h2>
        <ol class="steps md:flex justify-center items-start max-w-5xl mx-auto text-black">
          <li class="relative mb-12 md:m-0 md:px-12 text-center md:w-1/3">
            <span class="-mb-6 relative block text-secondary leading-none text-6xl font-semibold z-10">01</span>
            <picture class="img block mb-3">
              <source
                srcset="
                  ~assets/img/campaign/group-9@.png 1x,
                  ~assets/img/campaign/group-9@2x.png 2x,
                  ~assets/img/campaign/group-9@3x.png 3x"
              >
              <img src="~assets/img/campaign/group-9.png" alt="" width="194" class="mx-auto w-9/12">
            </picture>
            <p class="mb-4">
              <strong class="border-b-2 border-orange text-2xl md:text-xl font-semibold">{{ $t("step1.hd") }}</strong>
            </p>
            <i18n path="step1.body" tag="p" class="text-sm md:text-left">
              <template #newline-sp><br class="md:hidden"></template>
            </i18n>
          </li>
          <li class="relative mb-12 md:m-0 md:px-12 text-center md:w-1/3">
            <span class="-mb-6 relative block text-secondary leading-none text-6xl font-semibold z-10">02</span>
            <picture class="img block mb-3">
              <img src="~assets/img/campaign/group-11.svg" alt="" width="194" class="mx-auto w-9/12">
            </picture>
            <p class="mb-4">
              <strong class="border-b-2 border-orange text-2xl md:text-xl font-semibold">{{ $t("step2.hd") }}</strong>
            </p>
            <i18n path="step2.body" tag="p" class="text-sm md:text-left">
              <template #newline-sp><br class="md:hidden"></template>
            </i18n>
          </li>
          <li class="relative mb-12 md:m-0 md:px-12 text-center md:w-1/3">
            <span class="-mb-6 relative block text-secondary leading-none text-6xl font-semibold z-10">03</span>
            <picture class="img block mb-3">
              <source
                srcset="
                  ~assets/img/campaign/group-12@.png 1x,
                  ~assets/img/campaign/group-12@2x.png 2x,
                  ~assets/img/campaign/group-12@3x.png 3x"
              >
              <img src="~assets/img/campaign/group-12.png" alt="" width="194" class="mx-auto w-9/12">
            </picture>
            <p class="mb-4">
              <strong class="border-b-2 border-orange text-2xl md:text-xl font-semibold">{{ $t("step3.hd") }}</strong>
            </p>
            <i18n path="step3.body" tag="p" class="text-sm md:text-left">
              <template #newline-sp><br class="md:hidden"></template>
            </i18n>
          </li>
        </ol>
        <div class="my-12 text-center">
          <nuxt-link to="/" class="inline-flex items-center justify-center w-7/8 max-w-xs bg-secondary text-white py-4 px-4 rounded-full text-center">
            {{ $t("btn.more") }}
            <span class="material-icons align-middle ml-4">arrow_forward</span>
          </nuxt-link>
        </div>
      </div>
    </section>
    <div class="overflow-hidden bg-gray-100 py-12 text-center text-black">
      <div class="flex flex-wrap items-center container w-7/8 lg:w-auto mx-auto">
        <div class="max-w-xl mx-auto my-8">
          <i18n path="contact.head" tag="p" class="font-bold">
            <template #newline><br></template>
          </i18n>
          <p class="text-3xl md:text-5xl font-semibold mt-12 mx-auto">
            <a href="tel:0120-264-076" class="inline-flex items-center border-b-4 border-white">
              <span class="material-icons text-4xl" data-v-3ede2f32="">phone</span>
              0120-264-076
            </a>
          </p>
          <p class="mt-3 mb-12 text-sm">{{ $t("contact.hours") }}</p>
          <div class="text-center">
            <button class="inline-flex items-center justify-center max-w-xs bg-orange text-white py-4 px-4 rounded-full text-center" @click="openModalDialog">
              {{ $t("btn.register") }}
              <span class="material-icons align-middle ml-4">arrow_forward</span>
            </button>
          </div>
        </div>
        <div class="hidden md:block pr-4 pl-10 lg:px-12 w-1/2 lg:w-3/12 lg:order-first">
          <img class="mx-auto" src="~assets/img/index/solutions1.png" srcset="~assets/img/index/solutions1@2x.png 2x" alt="">
        </div>
        <div class="hidden md:block pr-10 pl-4 lg:px-12 w-1/2 lg:w-3/12">
          <img class="mx-auto" src="~assets/img/index/solutions2.png" srcset="~assets/img/index/solutions2@2x.png 2x" alt="">
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="sass" scoped>
@import '~assets/css/cms-forms.sass'
@import '~assets/css/markdown.sass'
.kv
  background-color: #d4f1ef
  &:before
    content: ''
    position: absolute
    top: 0
    left: 0
    width: 100%
    height: 100%
    background: url(~assets/img/campaign/bg_pat.png) repeat center 0
    background-size: 103px auto
    opacity: 0.6
  @screen md
    min-height: 583px
    &:before
      background-size: 185px auto
.steps
  li
    &:not(:first-child)
      @apply relative pt-16 mt-5
      &:before
        content: ''
        position: absolute
        top: 6px
        left: 50%
        width: 16px
        height: 16px
        border-top: 2px solid
        border-right: 2px solid
        transform: rotate(135deg) translate(50%, 50%)
        .howto &
          content: none
          display: none
    @screen md
      &:not(:first-child)
        @apply pt-0 mt-0
      &:before
        content: none
        display: none
      .img-box
        height: 125px
        @apply relative w-full mx-auto
        img
          object-fit: contain
          @apply absolute top-0 left-0 w-full h-full
      &:not(:first-child)
        .img-box,
        .img
          @apply relative
          &:before
            content: ''
            top: 50%
            left: -4rem
            width: 16px
            height: 16px
            border-top: 2px solid
            border-right: 2px solid
            transform: rotate(45deg) translate(-50%, -50%)
            @apply absolute
        .img-box
          &:before
            left: -2rem
</style>

<script>
import urljoin from "url-join";
import hsForm from "@/components/hsForm";
import contactSuccess from "@/components/contact/success";
import modalMixin from '@/components/mixins/modalHandler';

export default {
  data() {
    return {
      isSubmitted: false,
      loading: { portalId: false, formId: false },
      error: false,
      portalId: null
    }
  },
  mixins: [modalMixin],
  components: {
    hsForm,
    contactSuccess
  },
  async asyncData({ $strapi, params, error }) {
    let article
    try {
      const items = await $strapi.$http.$get(`campaigns?slug=${params.slug}`);
      article = items[0]
      if (!article) {
        throw new Error('Not found')
      }

    } catch (e) {
      error({
        statusCode: 404,
        message: e.message
      })
    }

    return {
      article
    };
  },
  created() {
    this.loading["portalId"] = true;
    this.$normalizeConfig("portalId")
      .then((pid) => { this.portalId = pid; })
      .finally(() => { this.loading["portalId"] = false });
  },
  computed: {
    isLoading () {
      return Object.values(this.loading).includes(true);
    },
    global() {
      return this.$store.getters['cms/global']
    },
    homepage() {
      return this.$store.getters['cms/homepage']
    }
  },
  head() {
    const { seo } = this.homepage;
    const { defaultSeo } = this.global;
    const fullSeo = {
      ...defaultSeo,
      ...seo
    };
    if (this.article) {
      fullSeo.metaTitle = this.article.title
      fullSeo.metaDescription = this.article.description
      if (this.article.ogImage) {
        fullSeo.shareImage = this.article.ogImage
      }
    }
    return {
      title: fullSeo.metaTitle,
      meta: this.$options.filters.cmsMetaTags(fullSeo)
    };
  },
  methods: {
    onSubmit() {
      this.$gtm.push({
        trackPageview: urljoin(this.$route.fullPath, 'thankyou'),
        title: this.$t('submitted_title'),
        event: 'loadready',
      });
      this.$hsq.pageView(urljoin(this.$route.fullPath, 'thankyou'));
      this.isSubmitted = true;
    },
    onIntersect(entries) {
      const target = entries.target;
      target.classList.toggle('invisible', !entries.isIntersecting);
    },
    zp(num, len) {
      return ( Array(len).join('0') + num ).slice( -len )
    }
  }
};
</script>